import React, { useEffect, useState } from "react";
import { Box, Grid, MenuItem, TextField, Typography } from "@mui/material";

import { getToken } from "../../../../api/Account";
import { getPlazoByPeriodicidadAsync } from "../../../../api/Plazo";
import { addPrestamoAsync } from "../../../../api/Employees";
import { EstadoError, useAviso } from "../../../../components/base";
import { formatearMonto } from "../../../../helpers/formato";
import {
  CampoMonto,
  DialogoFormulario,
  esMontoEscribible,
  useCargaLocal,
  useEnvio,
  useEscritura,
} from "../comunes";

// Misma fórmula de siempre: monto entre las cuotas del plazo, con 2 decimales. Sin monto o sin
// plazo la cuota es 0. Es lo que se guarda en el servidor, que no la recalcula.
const calcularCuota = (monto, plazoId, plazos) => {
  const valor = parseFloat(monto);
  if (valor > 0 && plazoId > 0) {
    const plazo = plazos.find((item) => Number(item.id) === Number(plazoId));
    if (plazo) return (valor / plazo.cuotas).toFixed(2);
  }
  return 0;
};

const AddPrestamo = ({ id, setShowModal, reload, setReload }) => {
  const [description, setDescription] = useState("");
  const [monto, setMonto] = useState("");
  const [plazoList, setPlazoList] = useState([]);
  const [selectedPlazo, setSelectedPlazo] = useState("");
  const [intentado, setIntentado] = useState(false);

  const token = getToken();
  const aviso = useAviso();
  const escribir = useEscritura();
  const { guardando, enviar } = useEnvio();
  const { cargando, error, cargar } = useCargaLocal(() =>
    getPlazoByPeriodicidadAsync(token, 2)
  );

  const getPlazoByPeriodicidad = async () => {
    const result = await cargar();
    if (result) setPlazoList(result.data.result);
  };

  useEffect(() => {
    getPlazoByPeriodicidad();
  }, []);

  const cuota = calcularCuota(monto, selectedPlazo, plazoList);

  const errorMonto = intentado && monto.length === 0 ? "Ingrese el monto del préstamo" : "";
  const errorPlazo = intentado && selectedPlazo === "" ? "Seleccione un plazo" : "";

  const saveChanges = () =>
    enviar(async () => {
      setIntentado(true);
      // Sin plazo el servidor no puede leer la solicitud (el plazo va como número): se avisa aquí.
      if (monto.length === 0 || selectedPlazo === "") return;

      const data = {
        employeeId: id,
        plazoId: selectedPlazo,
        monto,
        cuota,
        description,
      };

      const ok = await escribir(
        () => addPrestamoAsync(token, data),
        "No se pudo agregar el préstamo"
      );
      if (!ok) return;
      aviso.exito("Préstamo agregado");
      setReload(!reload);
      setShowModal(false);
    });

  const setMontoPrestamo = (valor) => {
    if (esMontoEscribible(valor)) setMonto(valor);
  };

  return (
    <DialogoFormulario
      titulo="Agregar préstamo"
      onCerrar={() => setShowModal(false)}
      hayCambios={monto !== "" || selectedPlazo !== "" || description !== ""}
      guardando={guardando}
      deshabilitado={cargando || Boolean(error)}
      onEnviar={saveChanges}
      textoEnviar="Agregar préstamo"
    >
      {error ? (
        <EstadoError
          titulo="No se pudieron cargar los plazos"
          mensaje="Sin ellos no se puede calcular la cuota ni agregar el préstamo. Revisa tu conexión e inténtalo de nuevo."
          onReintentar={getPlazoByPeriodicidad}
        />
      ) : (
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <CampoMonto
              autoFocus
              required
              label="Monto del préstamo"
              value={monto}
              onChange={setMontoPrestamo}
              error={errorMonto}
              disabled={guardando}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              required
              fullWidth
              label="Plazo"
              value={selectedPlazo}
              onChange={(e) => setSelectedPlazo(e.target.value)}
              error={Boolean(errorPlazo)}
              helperText={
                errorPlazo || (cargando ? "Cargando los plazos…" : " ")
              }
              disabled={guardando || cargando}
            >
              <MenuItem value="">
                <em>Seleccione un plazo</em>
              </MenuItem>
              {plazoList.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.description}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              label="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value.toLocaleUpperCase())}
              disabled={guardando}
              helperText=" "
            />
          </Grid>
          <Grid item xs={12}>
            {/* La cuota la calcula esta pantalla y se guarda tal cual: se ve antes de confirmar. */}
            <Box
              role="status"
              aria-live="polite"
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: "soft.primary",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Cuota por nómina
              </Typography>
              <Typography variant="monto" component="span" sx={{ color: "primary.main" }}>
                {formatearMonto(cuota)}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
              Se calcula con el monto y el plazo que elijas.
            </Typography>
          </Grid>
        </Grid>
      )}
    </DialogoFormulario>
  );
};

export default AddPrestamo;
